import { useProjectCreate } from './useProjectCreate'
import { useInternalGraphql } from '../useInternalGraphql'
import { useCallback } from 'react'
import { useRedirectToHomepage } from '../useRedirectToHomepage'
import { DEFAULT_STYLEGUIDE_VALUES } from '@sceneio/content-store/lib/features/project/projectDefaults'
import {
  setTriggerLimitsCheck,
  useSubscriptionDispatch,
} from '@sceneio/content-store/lib/features/subscription/subscriptionSlice'
import { findHighestUntitledProjectIndex } from './helpers/findHighestUntitledProjectIndex'

export function useAutoCreateProject() {
  const redirectToHomepage = useRedirectToHomepage()
  const createProject = useProjectCreate()
  const subscriptionDispatch = useSubscriptionDispatch()

  const { sendRequest: fetchWorkspaceProjectNames } = useInternalGraphql({
    query: 'UserAssignedProjectNamesByWorkspaceIdDocument',
    runOnInit: false,
  })

  return useCallback(
    async ({
      workspaceId,
      themeId,
      onSuccess,
      onError,
    }: {
      workspaceId: string
      themeId?: string | null | (string | null)[]
      onSuccess?: () => void
      onError?: () => void
    }) => {
      try {
        const { data } = await fetchWorkspaceProjectNames({
          variables: { workspaceId },
        })
        const projects = data?.assignedWorkspaces?.[0]?.assignedProjects || []

        const projectNames = projects.map((p) => p?.project?.name || '')
        const highestIndex = findHighestUntitledProjectIndex(projectNames)

        const newProjectName =
          highestIndex > 0 ? `Untitled ${highestIndex + 1}` : 'Untitled'

        await createProject(
          {
            name: newProjectName,
            workspaceId,
            preferences: {
              styleguide: DEFAULT_STYLEGUIDE_VALUES,
            },
          },
          {
            onSuccess: (data) => {
              const { id } = data || {}

              if (id) {
                redirectToHomepage({
                  projectId: id,
                  themeId,
                  onError: () => {
                    console.log(
                      '[useAutoCreateProject]: Error redirecting to project',
                    )
                    if (onError) {
                      onError()
                    }
                  },
                  onSuccess: () => {
                    if (onSuccess) {
                      onSuccess()
                    }
                  },
                })
              } else {
                console.log(
                  '[useAutoCreateProject]: Error redirecting to project id',
                )
                if (onError) {
                  onError()
                }
              }
              subscriptionDispatch(setTriggerLimitsCheck('remote'))
            },
          },
        )
      } catch (error) {
        console.error('[useAutoCreateProject]: Project creation failed:', error)
        if (onError) {
          onError()
        }
      }
    },
    [fetchWorkspaceProjectNames],
  )
}
